import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
} from '@angular/core';
import { EmojiPickerCategories, EmojiPickerData } from '@fi-shared/fi-emoji-button/fi-emoji-picker-data.enum';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { IEmoji, IEmojiMartOverlayData } from '@fi-shared/fi-emoji-button/fi-emoji';
import { DialogPosition, MatDialogConfig } from '@angular/material/dialog';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { FiLikesStoreService } from '@fi-app/shared/fi-likes/services/fi-likes-store.service';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-emoji-mart-overlay',
    template: `
        <emoji-mart
            [set]="emojiPickerData.Set"
            [sheetSize]="emojiPickerData.SheetSize"
            [emojiSize]="emojiPickerData.EmojiSize"
            [exclude]="getExcludedCategories()"
            [emojisToShowFilter]="emojisToShowFilter"
            [showPreview]="false"
            [perLine]="getEmojisRowLength()"
            [recent]="favoriteEmojiUnifiedCodes()"
            (emojiClick)="addEmoji($event)"
            [darkMode]="false"
            [i18n]="emojiI18n"
        >
        </emoji-mart>
    `,
    styleUrls: ['./fi-emoji-mart-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-emoji-mart-overlay' },
    standalone: false,
})
export class FiEmojiMartOverlayComponent
    extends FiDialogsContentBase<undefined, undefined>
    implements OnInit, OnDestroy
{
    protected emojiPickerData = EmojiPickerData;
    private matDialogConfig: MatDialogConfig = new MatDialogConfig();

    private readonly detectDevicesService = inject(DetectDevicesService);
    private readonly fiEmojiService = inject(FiEmojiService);
    private readonly fiLikesStoreService = inject(FiLikesStoreService);
    private readonly elementRef = inject(ElementRef);
    private readonly appTextService = inject(AppTextService);

    private currentTriggeredElement: ElementRef;
    private readonly defaultPosition: DialogPosition = {
        top: '100%',
        left: '100%',
    };
    private isEmojiOverlayShown: boolean = false;

    protected get favoriteEmojiUnifiedCodes() {
        return this.fiLikesStoreService.favoriteEmojiUnifiedCodes;
    }
    protected readonly emojiI18n = {
        search: this.appTextService.get('reaction.search_emojis'),
        notfound: this.appTextService.get('reaction.no_emojis_found'),
        categories: {
            search: this.appTextService.get('reaction.search_results'),
            recent: this.appTextService.get('reaction.recently_used'),
            people: this.appTextService.get('reaction.smileys_and_people'),
            nature: this.appTextService.get('reaction.animals_and_nature'),
            foods: this.appTextService.get('reaction.food_and_drink'),
            activity: this.appTextService.get('reaction.activity'),
            places: this.appTextService.get('reaction.travel_and_places'),
            objects: this.appTextService.get('reaction.objects'),
        },
    };

    ngOnInit(): void {
        this.updateOverlayPosition(this.defaultPosition);
        this.subscribeToOverlayVisibilityChange();
        this.subscribeOnPositionUpdate();
        this.fiEmojiService.emojiOverlayElementRef = this.elementRef;
    }

    public getExcludedCategories(): string[] {
        return [EmojiPickerCategories.Symbols, EmojiPickerCategories.Flags, EmojiPickerCategories.Custom];
    }

    public getEmojisRowLength(): number {
        return this.detectDevicesService.isMobileSmall()
            ? this.emojiPickerData.EmojisPerLineMobile
            : this.emojiPickerData.EmojisPerLineDesktop;
    }

    public addEmoji($emojiClickEvent: { $event: MouseEvent; emoji: IEmoji }): void {
        $emojiClickEvent.$event.preventDefault();
        this.fiEmojiService.triggerInsertEmoji($emojiClickEvent.emoji);
    }

    private updateOverlayPosition(position: DialogPosition): void {
        this.matDialogConfig.position = position;
        this.dialogRef.updatePosition(this.matDialogConfig.position);
    }

    private subscribeOnPositionUpdate(): void {
        this.fiEmojiService
            .onUpdateEmojiOverlayPosition$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                if (!isNullOrUndefined(this.currentTriggeredElement) && this.isEmojiOverlayShown)
                    this.calculateOverlayPosition();
            });
    }

    private subscribeToOverlayVisibilityChange(): void {
        this.fiEmojiService
            .onEmojiOverlayVisibilityChange$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((data: IEmojiMartOverlayData) => {
                this.currentTriggeredElement = data.triggerElement;
                this.isEmojiOverlayShown = data.isEmojiOverlayShown;
                this.isEmojiOverlayShown
                    ? this.calculateOverlayPosition()
                    : this.updateOverlayPosition(this.defaultPosition);
            });
    }

    private calculateOverlayPosition(): void {
        const rect: DOMRect = this.currentTriggeredElement.nativeElement.getBoundingClientRect();
        const overlayElement: HTMLElement = this.elementRef.nativeElement.parentElement;
        let topOffset: number = rect.top - overlayElement.clientHeight;
        const leftOffset: number = rect.left - overlayElement.clientWidth;

        if (topOffset + overlayElement.offsetHeight > window.innerHeight) {
            topOffset = window.innerHeight - overlayElement.clientHeight;
        } else {
            topOffset = topOffset > 0 ? topOffset : 0;
        }

        this.updateOverlayPosition({ top: `${topOffset}px`, left: `${leftOffset}px` });
    }

    protected emojisToShowFilter = (unified: string) => {
        if (!unified) {
            return false;
        }
        /*if (unified.includes('-')) {
            // for tests
            return true;
        }*/
        return true;
    };

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
